import React, { useEffect, useState } from 'react';
import { DigitalSubmissionMeta, FullSubmissionMap, QuestionCaseSetting } from '../../redux/types';
import { isAnswerEmpty } from '../../components/utilities/digitalQuestionUtil';
import { filterOptOutSects } from '../../components/utilities/digitalSurveyUtil';

type SettingMapType = Record<number, Record<number, QuestionCaseSetting> | undefined>;

const checkCompulsoryMainSubmissions = (
  settingsArr: QuestionCaseSetting[],
  fsMap: FullSubmissionMap,
) => {
  const pendingEntries = settingsArr
    .filter((s) => !!s.digitalCaseId)
    .map((s) => {
      const submission = fsMap[s.digitalSectionId]?.[s.digitalQuestionId]?.[s.digitalCaseId];
      if (isAnswerEmpty(submission) && s.compulsory) {
        return s;
      }
      return null;
    })
    .filter((s) => s !== null);
  return pendingEntries as QuestionCaseSetting[];
};

/**
 * The hook returns a dictionary; "`QuestionCaseSetting` by QuestionId and CaseId"
 * of unanswered compulsory questions. i.e. settings of those question and cases
 * which are compulsory but their `DigitalSubmission` does not exist
 *
 * @param settings QuestionCaseSettings list
 * @param fullSubmissionMap DigitalSubmission dictionary by sectionId.QuestionId.caseId
 * @param dsms DigitalSubmissionMetas of the survey
 * @returns QuestionCaseSetting map by QuestionId.CaseId and pending SectionId
 */
const usePendingCompulsoryQuestions = (
  settings: QuestionCaseSetting[],
  fullSubmissionMap: FullSubmissionMap,
  dsms?: DigitalSubmissionMeta[],
): [SettingMapType?, number?] => {
  const [
    incompleteSettingMap,
    setIncompleteSettingMap,
  ] = useState<SettingMapType>();
  const [pendingSecId, setPendingSecId] = useState<number>();

  useEffect(() => {
    const pendingEntries = filterOptOutSects(
      checkCompulsoryMainSubmissions(settings, fullSubmissionMap), dsms,
    );
    if (!pendingEntries.length) {
      setIncompleteSettingMap(undefined);
      setPendingSecId(undefined);
    } else {
      // set the first pending section id that it sees
      const pendingEntry = pendingEntries[0];
      setPendingSecId(pendingEntry.digitalSectionId);
      const settingMap = pendingEntries.reduce((acc, cur) => {
        acc[cur.digitalQuestionId] = {
          ...acc[cur.digitalQuestionId],
          [cur.digitalCaseId]: cur,
        };
        return acc;
      }, {} as SettingMapType);
      setIncompleteSettingMap(pendingEntries.length ? settingMap : undefined);
    }
  }, [fullSubmissionMap, settings, dsms]);

  return [incompleteSettingMap, pendingSecId];
};

export default usePendingCompulsoryQuestions;
